import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';

const LOCAL_STORAGE_KEY = 'chownow:returnTo';

interface ReturnToRedirect {
  pathname: string;
  expires: number;
}

export function serializeReturnToRedirect(pathname: string) {
  return JSON.stringify({
    pathname,
    expires: dayjs().add(1, 'hour').valueOf(),
  } satisfies ReturnToRedirect);
}

export function isReturnToRedirect(input: unknown): input is ReturnToRedirect {
  return (
    !!input &&
    typeof input === 'object' &&
    'pathname' in input &&
    typeof input.pathname === 'string' &&
    'expires' in input &&
    typeof input.expires === 'number'
  );
}

export function deserializeReturnToRedirect(rawRedirect: string) {
  try {
    const parsedRedirect = JSON.parse(rawRedirect) as unknown;

    if (!isReturnToRedirect(parsedRedirect)) {
      throw new Error('invalid or unexpected raw ReturnToRedirect');
    }

    return parsedRedirect;
  } catch (e) {
    Sentry.captureException(e, { extra: { rawRedirect } });
  }

  return undefined;
}

export function getReturnToRedirect() {
  const rawRedirect = localStorage.getItem(LOCAL_STORAGE_KEY);

  if (!rawRedirect) {
    return undefined;
  }

  // always clear the key as it should only ever be read & redirected once
  // this will also clear invalid, unexpected, or expired redirects
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  const redirect = deserializeReturnToRedirect(rawRedirect);

  if (!redirect || redirect.expires < Date.now()) {
    return undefined;
  }

  return redirect.pathname;
}

export function writeReturnToRedirect(pathname: string) {
  localStorage.setItem(LOCAL_STORAGE_KEY, serializeReturnToRedirect(pathname));
}
